import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { StaticRouter, Route } from 'react-router';
import { getContext } from '@fiverr-private/fiverr_context';
import { SafeWrapper } from '@fiverr-private/seller_pages_toolbox';
import { BASE_NAME, Routes } from '../constants';
import { FreelancerPage } from '../components/FreelancerPage';
import { FreelancerPageContextProvider, FreelancerPageContextProps } from '../components/FreelancerPageContext';
import { AppProvidersLayerProps, AppProvidersLayer } from '../layers';
import { HighlightsData } from '../types';

export interface FreelancerPageAppProps extends FreelancerPageContextProps {
    currency: AppProvidersLayerProps['currency'];
    rollouts: AppProvidersLayerProps['rollouts'];
    entryType: 'client' | 'server';
    floatingChatData: AppProvidersLayerProps['floatingChatData'];
}

const RouterResolver: React.FC<React.PropsWithChildren<Pick<FreelancerPageAppProps, 'entryType'>>> = ({
    entryType,
    children,
}) =>
    entryType === 'client' ? (
        <BrowserRouter basename={BASE_NAME}>{children}</BrowserRouter>
    ) : (
        <StaticRouter basename={BASE_NAME} context={{}}>
            {children}
        </StaticRouter>
    );

const FreelancerPageApp: React.FC<FreelancerPageAppProps> = ({
    sellerPageData,
    reviewsData,
    gigsData,
    consultationData,
    trustedFreelancerData,
    currency,
    customOrdersAllowed,
    isEligibleSeller,
    isActivationCompleted,
    notableClients = [],
    highlightsData = {} as HighlightsData,
    rollouts = {
        inMustangsUpdateProfilePhotoRollout: false,
    },
    entryType,
    inVettedBadgeTest,
    localizationData,
    pushkinConfiguration,
    bannerData,
    isExperiential,
    loyaltyData,
    complianceData,
    floatingChatData,
}) => {
    const {
        pathParameters: { username },
    } = getContext();

    return (
        <AppProvidersLayer
            rollouts={rollouts}
            currency={currency}
            seller={sellerPageData}
            floatingChatData={floatingChatData}
        >
            <RouterResolver entryType={entryType}>
                <Route path={Routes.Root}>
                    <FreelancerPageContextProvider
                        complianceData={complianceData}
                        username={username}
                        customOrdersAllowed={customOrdersAllowed}
                        consultationData={consultationData}
                        currency={currency}
                        isEligibleSeller={isEligibleSeller}
                        sellerPageData={sellerPageData}
                        notableClients={notableClients}
                        gigsData={gigsData}
                        trustedFreelancerData={trustedFreelancerData}
                        highlightsData={highlightsData}
                        reviewsData={reviewsData}
                        isActivationCompleted={isActivationCompleted}
                        localizationData={localizationData}
                        inVettedBadgeTest={inVettedBadgeTest}
                        pushkinConfiguration={pushkinConfiguration}
                        bannerData={bannerData}
                        isExperiential={isExperiential}
                        loyaltyData={loyaltyData}
                    >
                        <FreelancerPage />
                    </FreelancerPageContextProvider>
                </Route>
            </RouterResolver>
        </AppProvidersLayer>
    );
};

export default SafeWrapper(FreelancerPageApp, 'FreelancerPageApp');
